import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Brain, Zap, Shield, TrendingUp, Search, FileText, Users, Bot, X, ArrowLeft, LayoutDashboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

interface NeuronNode {
  id: string;
  label: string;
  icon: React.ReactNode;
  x: number;
  y: number;
  color: string;
  description: string;
}

const NODES: NeuronNode[] = [
  { id: "analyste", label: "Analyste", icon: <TrendingUp className="h-4 w-4" />, x: 50, y: 15, color: "hsl(var(--primary))", description: "Briefings stratégiques quotidiens, analyse KPIs" },
  { id: "veilleur", label: "Veilleur", icon: <Shield className="h-4 w-4" />, x: 85, y: 35, color: "hsl(220, 90%, 56%)", description: "Détection d'anomalies 24/7, alertes en temps réel" },
  { id: "prospecteur", label: "Prospecteur", icon: <Search className="h-4 w-4" />, x: 80, y: 70, color: "hsl(150, 80%, 45%)", description: "Scan LinkedIn & web, identification de leads qualifiés" },
  { id: "redacteur", label: "Rédacteur", icon: <FileText className="h-4 w-4" />, x: 50, y: 85, color: "hsl(280, 70%, 55%)", description: "Séquences d'emails, contenu marketing automatisé" },
  { id: "qualificateur", label: "Qualificateur", icon: <Users className="h-4 w-4" />, x: 20, y: 70, color: "hsl(30, 90%, 55%)", description: "Scoring contacts, priorisation des prospects chauds" },
  { id: "closer", label: "Closer", icon: <Zap className="h-4 w-4" />, x: 15, y: 35, color: "hsl(350, 80%, 55%)", description: "Négociation, closing automatisé, suivi pipeline" },
];

const CONNECTIONS = [
  ["analyste", "veilleur"], ["analyste", "closer"], ["veilleur", "prospecteur"],
  ["prospecteur", "redacteur"], ["redacteur", "qualificateur"], ["qualificateur", "closer"],
  ["analyste", "qualificateur"], ["veilleur", "redacteur"], ["prospecteur", "closer"],
];

export function GraftonBrain({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [activeNode, setActiveNode] = useState<string | null>(null);
  const [pulsingConnections, setPulsingConnections] = useState<number[]>([]);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!open) return;
    intervalRef.current = setInterval(() => {
      const count = Math.floor(Math.random() * 3) + 1;
      const indices = Array.from({ length: count }, () => Math.floor(Math.random() * CONNECTIONS.length));
      setPulsingConnections(indices);
    }, 1200);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [open]);

  const getNode = (id: string) => NODES.find(n => n.id === id);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: "spring", damping: 20 }}
            className="relative w-[95vw] max-w-3xl aspect-square max-h-[80vh] rounded-3xl bg-gradient-to-br from-background via-background to-muted/30 border border-border/50 shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Navigation buttons */}
            <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
              <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => { onClose(); navigate("/dashboard"); }}>
                <LayoutDashboard className="h-3.5 w-3.5" />
                Dashboard
              </Button>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Title */}
            <div className="absolute top-6 left-6 z-10">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Brain className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-foreground">Cerveau de Grafton</h2>
                  <p className="text-[10px] text-muted-foreground">Intelligence autonome · 6 agents actifs</p>
                </div>
              </div>
            </div>

            {/* Neural network SVG */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
              <defs>
                {CONNECTIONS.map((_, i) => (
                  <linearGradient key={`grad-${i}`} id={`conn-grad-${i}`}>
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.3" />
                    <stop offset="50%" stopColor="hsl(var(--primary))" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.3" />
                  </linearGradient>
                ))}
                <filter id="glow">
                  <feGaussianBlur stdDeviation="0.5" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              {/* Connections */}
              {CONNECTIONS.map(([fromId, toId], i) => {
                const from = getNode(fromId);
                const to = getNode(toId);
                if (!from || !to) return null;
                const isPulsing = pulsingConnections.includes(i);
                const isHighlighted = activeNode && (fromId === activeNode || toId === activeNode);
                return (
                  <g key={`conn-${i}`}>
                    <line
                      x1={from.x} y1={from.y} x2={to.x} y2={to.y}
                      stroke={isHighlighted ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))"}
                      strokeWidth={isHighlighted ? 0.4 : 0.15}
                      strokeOpacity={isHighlighted ? 0.9 : 0.25}
                    />
                    {isPulsing && (
                      <circle r="0.6" fill="hsl(var(--primary))" filter="url(#glow)">
                        <animateMotion dur="1.5s" repeatCount="1" fill="freeze">
                          <mpath href={`#path-${i}`} />
                        </animateMotion>
                        <animate attributeName="opacity" values="1;0" dur="1.5s" repeatCount="1" fill="freeze" />
                      </circle>
                    )}
                    <path id={`path-${i}`} d={`M${from.x},${from.y} L${to.x},${to.y}`} fill="none" />
                  </g>
                );
              })}

              {/* Central brain core */}
              <circle cx="50" cy="50" r="6" fill="none" stroke="hsl(var(--primary))" strokeWidth="0.2" strokeOpacity="0.3">
                <animate attributeName="r" values="5;7;5" dur="3s" repeatCount="indefinite" />
                <animate attributeName="strokeOpacity" values="0.2;0.5;0.2" dur="3s" repeatCount="indefinite" />
              </circle>
              <circle cx="50" cy="50" r="3" fill="none" stroke="hsl(var(--primary))" strokeWidth="0.15" strokeOpacity="0.5">
                <animate attributeName="r" values="3;4;3" dur="2s" repeatCount="indefinite" />
              </circle>
              <circle cx="50" cy="50" r="1.5" fill="hsl(var(--primary))" fillOpacity="0.15">
                <animate attributeName="fillOpacity" values="0.1;0.3;0.1" dur="2s" repeatCount="indefinite" />
              </circle>
            </svg>

            {/* Agent nodes as HTML */}
            {NODES.map((node) => (
              <motion.div
                key={node.id}
                className="absolute cursor-pointer group"
                style={{ left: `${node.x}%`, top: `${node.y}%`, transform: "translate(-50%, -50%)" }}
                whileHover={{ scale: 1.15 }}
                onMouseEnter={() => setActiveNode(node.id)}
                onMouseLeave={() => setActiveNode(null)}
              >
                <div className={`relative flex flex-col items-center gap-1 transition-all duration-300 ${activeNode === node.id ? "z-20" : "z-10"}`}>
                  <div
                    className="h-10 w-10 rounded-full flex items-center justify-center shadow-lg border border-border/50 transition-shadow duration-300"
                    style={{
                      background: `linear-gradient(135deg, ${node.color}22, ${node.color}44)`,
                      boxShadow: activeNode === node.id ? `0 0 20px ${node.color}66` : `0 0 8px ${node.color}22`,
                    }}
                  >
                    <div style={{ color: node.color }}>{node.icon}</div>
                  </div>
                  <span className="text-[9px] font-semibold text-foreground/80 whitespace-nowrap">{node.label}</span>
                  
                  {/* Tooltip */}
                  <AnimatePresence>
                    {activeNode === node.id && (
                      <motion.div
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 5 }}
                        className="absolute top-full mt-2 px-3 py-2 bg-popover border border-border rounded-lg shadow-xl max-w-[180px] z-30"
                      >
                        <p className="text-[10px] text-muted-foreground leading-tight">{node.description}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Pulse ring */}
                <div
                  className="absolute inset-0 rounded-full animate-ping opacity-20"
                  style={{ background: node.color, animationDuration: "3s" }}
                />
              </motion.div>
            ))}

            {/* Central brain icon */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                className="h-14 w-14 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center backdrop-blur-sm"
              >
                <Bot className="h-7 w-7 text-primary" />
              </motion.div>
              <p className="text-center text-[8px] font-bold text-primary mt-1 tracking-wider">GRAFTON IA</p>
            </div>

            {/* Status bar */}
            <div className="absolute bottom-4 left-4 right-4 z-10 flex items-center justify-between px-4 py-2 bg-muted/50 backdrop-blur rounded-xl border border-border/50">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-[10px] text-muted-foreground">Système actif · 24/7</span>
              </div>
              <div className="flex gap-3 text-[10px] text-muted-foreground">
                <span>🔄 Cycle autonome</span>
                <span>🌍 Global</span>
                <span>⚡ Temps réel</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
